import { z } from 'zod';

/**
 * DPIA coverage per cloud service: every active `software` or `suppliers`
 * record flagged `isCloudService` paired with the DPIA (`dpias` record) that
 * covers it, and a status derived from the DPIA's outcome and review date.
 * Read-only view computed from org_records by GET /orgs/:orgId/dpia-coverage
 * (apps/worker/src/routes/dpia-coverage.ts); nothing is stored.
 */

export const DPIA_COVERAGE_STATUSES = ['covered', 'pending', 'missing', 'review_overdue'] as const;
export const DpiaCoverageStatusSchema = z.enum(DPIA_COVERAGE_STATUSES);
export type DpiaCoverageStatus = z.infer<typeof DpiaCoverageStatusSchema>;
export const DPIA_COVERAGE_STATUS_LABELS: Record<DpiaCoverageStatus, string> = {
  covered: 'Covered',
  pending: 'Pending',
  missing: 'Missing',
  review_overdue: 'Review overdue',
};

/** Collections whose records can be cloud services. */
export const DPIA_COVERAGE_COLLECTIONS = ['software', 'suppliers'] as const;
export const DpiaCoverageCollectionSchema = z.enum(DPIA_COVERAGE_COLLECTIONS);
export type DpiaCoverageCollection = z.infer<typeof DpiaCoverageCollectionSchema>;

export const DpiaCoverageItemSchema = z.object({
  recordId: z.string(),
  collection: DpiaCoverageCollectionSchema,
  name: z.string(),
  /** `dataLocation` for software, `processingLocations` for suppliers. */
  dataLocation: z.array(z.string()),
  dataLocationDetail: z.string(),
  accessInternal: z.string(),
  accessExternal: z.string(),
  /** The linked DPIA (either side may hold the reference); null when none. */
  dpiaRecordId: z.string().nullable(),
  dpiaName: z.string().nullable(),
  /** The DPIA's `outcome` ('Pending' | 'Approved' | 'Approved with conditions' | 'Rejected'); null when unset. */
  dpiaOutcome: z.string().nullable(),
  /** The DPIA's `reviewDate` (YYYY-MM-DD); null when unset. */
  dpiaReviewDate: z.string().nullable(),
  status: DpiaCoverageStatusSchema,
});
export type DpiaCoverageItem = z.infer<typeof DpiaCoverageItemSchema>;

export const DpiaCoverageCountsSchema = z.object({
  covered: z.number().int(),
  pending: z.number().int(),
  missing: z.number().int(),
  reviewOverdue: z.number().int(),
  total: z.number().int(),
});
export type DpiaCoverageCounts = z.infer<typeof DpiaCoverageCountsSchema>;

export const DpiaCoverageSchema = z.object({
  items: z.array(DpiaCoverageItemSchema),
  counts: DpiaCoverageCountsSchema,
});
export type DpiaCoverage = z.infer<typeof DpiaCoverageSchema>;
